import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AlertOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/currency';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import { DashboardDataDto } from '../dto/report';
import { OrderDto } from '../dto/order';
import { fetchDashboardData as fetchDashboardDataFromApi } from '../modules/dashboard/dashboardApi';
import { isTesterMode } from '../modules/tester/testerModeAccessPolicy';
import {
  DashboardBucketViewModel,
  buildDashboardBucketLink,
  buildDashboardBucketViewModels,
  buildDashboardDeadlineBucket,
  buildDashboardStatusCards,
} from '../modules/dashboard/dashboardWorkflow';
import CustomerPaymentsPanel from '../modules/dashboard/CustomerPaymentsPanel';
import { ORDER_STATUS_COLORS } from '../utils/orderStatus';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import './Dashboard.css';

const { Text, Title } = Typography;

const BUCKET_ICONS: Record<string, React.ReactNode> = {
  alert: <AlertOutlined />,
  calendar: <CalendarOutlined />,
  clock: <ClockCircleOutlined />,
  fileDone: <FileDoneOutlined />,
  truck: <TruckOutlined />,
};

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'orders']);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [data, setData] = useState<DashboardDataDto | null>(null);
  const user = authService.getCurrentUser();
  const isTester = isTesterMode(user);
  const canViewFullKpis = authService.canViewDashboardFullKpis();

  const loadDashboardData = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      setData(await fetchDashboardDataFromApi());
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setRefreshError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isTester) loadDashboardData();
    else setLoading(false);
  }, [loadDashboardData, isTester]);

  usePageAutoRefresh(loadDashboardData, {
    enabled: !isTester,
    intervalMs: 60_000,
  });

  const renderStatus = (status: string) => (
    <Tag color={ORDER_STATUS_COLORS[status] || 'default'}>
      {t(`orders:statusOptions.${status}`, { defaultValue: status.replace(/_/g, ' ') })}
    </Tag>
  );

  const orderLink = (order: OrderDto) => (
    <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber || order.orderNumber}</Link>
  );

  const bucketIcon = (bucket: Pick<DashboardBucketViewModel, 'icon'>) =>
    (bucket.icon && BUCKET_ICONS[bucket.icon]) || <AlertOutlined />;

  const dashboardModel = useMemo(() => {
    const operationalBuckets = buildDashboardBucketViewModels(data?.operationalBuckets || [], 'operational');
    const sideBuckets = buildDashboardBucketViewModels(data?.sideBuckets || [], 'side');
    const deadlineBucket = buildDashboardDeadlineBucket(data?.upcomingDeadlineCount || 0);
    const statusCards = buildDashboardStatusCards(data?.statusCounts || {}, operationalBuckets, sideBuckets, data?.overdueCount || 0);
    const findBucket = (key: string) => operationalBuckets.find((bucket) => bucket.key === key);
    const findSideBucket = (key: string) => sideBuckets.find((bucket) => bucket.key === key);
    const attention = findBucket('attention');
    const dispatch = findBucket('dispatch');
    const transport = findBucket('transport');
    const production = findSideBucket('production');

    return {
      operationalBuckets,
      sideBuckets,
      deadlineBucket,
      statusCards,
      attention,
      dispatch,
      transport,
      production,
    };
  }, [data]);

  const monthlyRevenue = data?.stats.monthlyRevenue || 0;
  const monthlyProfit = data?.stats.monthlyProfit || 0;
  const averageMargin = monthlyRevenue > 0 ? (monthlyProfit / monthlyRevenue) * 100 : 0;
  const customerPayments = data?.customerPayments;
  const upcomingDeadlines = data?.upcomingDeadlines || [];
  const deadlineCountsByDate = new Map((data?.upcomingDeadlineDays || []).map((day) => [day.date, day.count]));
  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const date = dayjs().add(index, 'day');
    const key = date.format('YYYY-MM-DD');
    const count = deadlineCountsByDate.get(key) || 0;

    return {
      key,
      date,
      count,
      label: index === 0 ? t('dashboard:week.today') : date.format('DD.MM'),
    };
  });

  const priorityItems = [
    {
      key: 'overdue',
      label: t('dashboard:priorities.overdue'),
      meta: t('dashboard:risks.oldest', { count: data?.overdueCount || 0 }),
      value: data?.overdueCount || 0,
      icon: <ExclamationCircleOutlined />,
      href: '/app/orders?deadline=overdue',
      tone: 'danger',
    },
    {
      key: 'documents',
      label: t('dashboard:priorities.documents'),
      meta: t('dashboard:kpi.documentsMeta'),
      value: dashboardModel.dispatch?.count || 0,
      icon: <FileDoneOutlined />,
      href: dashboardModel.dispatch ? buildDashboardBucketLink(dashboardModel.dispatch) : '/app/orders',
      tone: 'warning',
    },
    {
      key: 'transport',
      label: t('dashboard:priorities.transport'),
      meta: t('dashboard:kpi.inTransportMeta'),
      value: dashboardModel.transport?.count || 0,
      icon: <TruckOutlined />,
      href: dashboardModel.transport ? buildDashboardBucketLink(dashboardModel.transport) : '/app/orders',
      tone: 'success',
    },
    {
      key: 'production',
      label: t('dashboard:priorities.production'),
      meta: t('dashboard:risks.count', { count: dashboardModel.production?.count || 0 }),
      value: dashboardModel.production?.count || 0,
      icon: <ClockCircleOutlined />,
      href: dashboardModel.production ? buildDashboardBucketLink(dashboardModel.production) : '/app/orders',
      tone: 'neutral',
    },
  ];

  const topCards = [
    {
      key: 'total',
      label: t('dashboard:stats.totalOrders'),
      value: data?.stats.totalOrders || 0,
      icon: <ShoppingCartOutlined />,
      href: '/app/orders',
      tone: 'blue',
      meta: t('dashboard:kpi.allOrdersMeta'),
    },
    {
      key: 'active',
      label: t('dashboard:stats.activeOrders'),
      value: data?.stats.activeOrders || 0,
      icon: <CheckSquareOutlined />,
      href: '/app/orders',
      tone: 'amber',
      meta: t('dashboard:kpi.activeOrdersMeta'),
    },
    {
      key: 'transport',
      label: t('dashboard:kpi.inTransport'),
      value: dashboardModel.transport?.count || 0,
      icon: <TruckOutlined />,
      href: dashboardModel.transport ? buildDashboardBucketLink(dashboardModel.transport) : '/app/orders',
      tone: 'green',
      meta: t('dashboard:kpi.inTransportMeta'),
    },
    {
      key: 'deadlines',
      label: t('dashboard:workQueue.cards.deadlines'),
      value: dashboardModel.deadlineBucket.count,
      icon: <CalendarOutlined />,
      href: buildDashboardBucketLink(dashboardModel.deadlineBucket),
      tone: 'violet',
      meta: t('dashboard:kpi.deadlinesMeta'),
    },
    {
      key: 'documents',
      label: t('dashboard:kpi.documentsToIssue'),
      value: dashboardModel.dispatch?.count || 0,
      icon: <FileTextOutlined />,
      href: dashboardModel.dispatch ? buildDashboardBucketLink(dashboardModel.dispatch) : '/app/orders',
      tone: 'orange',
      meta: t('dashboard:kpi.documentsMeta'),
    },
    {
      key: 'overdue',
      label: t('dashboard:kpi.overdueTasks'),
      value: data?.overdueCount || 0,
      icon: <WarningOutlined />,
      href: '/app/orders?deadline=overdue',
      tone: 'slate',
      meta: t('dashboard:kpi.overdueMeta'),
    },
  ];

  const queueColumns: ColumnsType<OrderDto> = [
    {
      title: t('dashboard:recentOrders.orderNumber'),
      key: 'orderNumber',
      render: (_, order) => orderLink(order),
    },
    {
      title: t('dashboard:recentOrders.customer'),
      key: 'customer',
      render: (_, order) => order.customer?.id ? (
        <Link to={`/app/customers/${order.customer.id}`}>{order.customerName || order.customer.name}</Link>
      ) : (
        order.customerName || '-'
      ),
    },
    {
      title: t('dashboard:recentOrders.status'),
      dataIndex: 'status',
      key: 'status',
      render: renderStatus,
    },
    ...(canViewFullKpis ? [
      {
        title: t('dashboard:recentOrders.revenue'),
        dataIndex: 'revenue',
        key: 'revenue',
        render: (value: number) => value ? formatCurrency(value, 'PLN') : '-',
      },
      {
        title: t('dashboard:recentOrders.margin'),
        dataIndex: 'marginPercent',
        key: 'marginPercent',
        render: (value: number) => value !== undefined ? (
          <span className={value < 15 ? 'dashboard-value-danger' : 'dashboard-value-success'}>
            {formatLocalizedPercent(value, i18n.language)}
          </span>
        ) : '-',
      },
    ] : []),
    {
      title: t('dashboard:table.action'),
      key: 'action',
      render: (_, order) => (
        <Link className="dashboard-icon-link" to={`/app/orders/${order.id}`} aria-label={t('dashboard:table.openOrder')}>
          <EyeOutlined />
        </Link>
      ),
    },
  ];

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (isTester) {
    return (
      <div className="dashboard-page dashboard-page--focused">
        <section className="dashboard-shell">
          <header className="dashboard-titlebar">
            <div>
              <Title level={2}>{t('dashboard:title')}</Title>
              <Text type="secondary">{t('dashboard:welcome', { name: user?.name || 'User' })}</Text>
            </div>
          </header>
          <Card className="dashboard-empty-state">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard:tester.message')}>
              <Space wrap>
                <Button type="primary" href="/app/orders">{t('dashboard:tester.createOrder')}</Button>
                <Button href="/app/customers">{t('dashboard:tester.customers')}</Button>
              </Space>
            </Empty>
          </Card>
        </section>
      </div>
    );
  }

  if (!data) {
    return (
      <Alert
        message={t('dashboard:error.loadingFailed')}
        type="error"
        showIcon
        action={(
          <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={loadDashboardData}>
            {t('common:refresh')}
          </Button>
        )}
      />
    );
  }

  return (
    <div className="dashboard-page">
      <section className="dashboard-shell">
        {refreshError && (
          <Alert
            message={t('dashboard:error.loadingFailed')}
            description={t('dashboard:error.showingPreviousData')}
            type="warning"
            showIcon
            closable
            action={(
              <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={loadDashboardData}>
                {t('common:refresh')}
              </Button>
            )}
          />
        )}
        {canViewFullKpis && (
          <section className="dashboard-kpi-grid">
            {topCards.map((card) => (
              <Link key={card.key} to={card.href} className={`dashboard-kpi-card dashboard-kpi-card--${card.tone}`}>
                <span className="dashboard-kpi-card__icon">{card.icon}</span>
                <span className="dashboard-kpi-card__body">
                  <span className="dashboard-kpi-card__label">{card.label}</span>
                  <strong>{card.value.toLocaleString()}</strong>
                  <span className="dashboard-kpi-card__meta">{card.meta}</span>
                </span>
              </Link>
            ))}
          </section>
        )}

        {canViewFullKpis && customerPayments && (
          <CustomerPaymentsPanel customerPayments={customerPayments} />
        )}

        {canViewFullKpis && (
          <section className="dashboard-section dashboard-finance-band">
            <div className="dashboard-section__header">
              <Title level={4}>{t('dashboard:financial.title')}</Title>
              <Link to="/app/reports">{t('dashboard:financial.reports')} <ArrowRightOutlined /></Link>
            </div>
            <div className="dashboard-finance-grid">
              <div className="dashboard-finance-metric">
                <Text>{t('dashboard:stats.monthlyRevenue')}</Text>
                <strong>{formatCurrency(monthlyRevenue, 'PLN')}</strong>
                <span>{t('dashboard:finance.monthScope')}</span>
              </div>
              <div className="dashboard-finance-metric">
                <Text>{t('dashboard:stats.monthlyProfit')}</Text>
                <strong>{formatCurrency(monthlyProfit, 'PLN')}</strong>
                <span>{t('dashboard:finance.monthScope')}</span>
              </div>
              <div className="dashboard-finance-metric">
                <Text>{t('dashboard:finance.averageMargin')}</Text>
                <strong>{formatLocalizedPercent(averageMargin, i18n.language, 2)}</strong>
                <span>{t('dashboard:finance.calculatedFromVisibleMonth')}</span>
              </div>
            </div>
          </section>
        )}

        <section className="dashboard-content-grid">
          <div className="dashboard-main-stack">
            <div className="dashboard-main-top">
              <section className="dashboard-section dashboard-priorities-section">
                <div className="dashboard-section__header">
                  <Title level={4}>{t('dashboard:priorities.title')}</Title>
                  <Link to="/app/tasks">{t('dashboard:priorities.openTasks')} <ArrowRightOutlined /></Link>
                </div>
                <div className="dashboard-priority-grid">
                  {priorityItems.map((item) => (
                    <Link key={item.key} to={item.href} className={`dashboard-priority-item dashboard-priority-item--${item.tone}`}>
                      <span className="dashboard-priority-item__icon">{item.icon}</span>
                      <span className="dashboard-priority-item__body">
                        <strong>{item.label}</strong>
                        <Text type="secondary">{item.meta}</Text>
                      </span>
                      <span className="dashboard-priority-item__value">{item.value.toLocaleString()}</span>
                    </Link>
                  ))}
                </div>
              </section>

              <section className="dashboard-section dashboard-week-section">
                <div className="dashboard-section__header">
                  <Title level={4}>{t('dashboard:week.title')}</Title>
                  <Text type="secondary">{t('dashboard:week.total', { count: dashboardModel.deadlineBucket.count })}</Text>
                </div>
                <div className="dashboard-week-strip">
                  {weekDays.map((day) => (
                    <Link key={day.key} to="/app/orders?deadline=upcoming" className={day.count > 0 ? 'dashboard-week-day dashboard-week-day--active' : 'dashboard-week-day'}>
                      <span>{day.label}</span>
                      <strong>{day.count}</strong>
                      <Text type="secondary">{t('dashboard:week.ordersShort')}</Text>
                    </Link>
                  ))}
                </div>
                <div className="dashboard-week-next">
                  <CalendarOutlined />
                  <span>
                    <strong>{t('dashboard:week.next')}</strong>
                    <Text type="secondary">
                      {upcomingDeadlines[0]?.estimatedCompletionDate ? dayjs(upcomingDeadlines[0].estimatedCompletionDate).format('YYYY-MM-DD') : '-'}
                    </Text>
                  </span>
                  <Link to={buildDashboardBucketLink(dashboardModel.deadlineBucket)}>{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
                </div>
              </section>
            </div>

            <section className="dashboard-section dashboard-status-section">
              <div className="dashboard-section__header">
                <Title level={4}>{t('dashboard:statusCards.title')}</Title>
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              </div>
              <div className="dashboard-status-grid">
                {dashboardModel.statusCards.map((card) => (
                  <Link key={card.key} to={card.href} className={`dashboard-status-card dashboard-status-card--${card.tone}`}>
                    <Text type="secondary">{t(card.labelKey)}</Text>
                    <strong>{card.value.toLocaleString()}</strong>
                  </Link>
                ))}
              </div>
            </section>

            <section className="dashboard-section dashboard-recent-section">
              <div className="dashboard-section__header">
                <Title level={4}>{t('dashboard:recentOrders.title')}</Title>
                <Link to="/app/orders">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>
              </div>
              <Table
                dataSource={data.recentOrders.slice(0, 5)}
                columns={queueColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </section>
          </div>

          <aside className="dashboard-right-stack">
            <section className="dashboard-section">
              <div className="dashboard-section__header">
                <Title level={4}>{canViewFullKpis ? t('dashboard:risks.combinedTitle') : t('dashboard:sidePanel.title')}</Title>
                {canViewFullKpis && <Link to="/app/reports">{t('dashboard:viewAll')} <ArrowRightOutlined /></Link>}
              </div>
              <div className="dashboard-risk-list">
                <div className="dashboard-risk-group">
                  <Link to="/app/orders?deadline=overdue" className="dashboard-risk-row dashboard-risk-row--danger">
                    <ExclamationCircleOutlined />
                    <span className="dashboard-risk-row__body">
                      <strong>{t('dashboard:sidePanel.overdue')}</strong>
                      <Text type="secondary">{t('dashboard:risks.oldest', { count: data.overdueCount })}</Text>
                    </span>
                    <ArrowRightOutlined />
                  </Link>
                  {dashboardModel.sideBuckets.slice(0, 2).map((bucket) => (
                    <Link key={bucket.key} to={buildDashboardBucketLink(bucket)} className="dashboard-risk-row">
                      {bucketIcon(bucket)}
                      <span className="dashboard-risk-row__body">
                        <strong>{t(bucket.labelKey)}</strong>
                        <Text type="secondary">{t('dashboard:risks.count', { count: bucket.count })}</Text>
                      </span>
                      <ArrowRightOutlined />
                    </Link>
                  ))}
                </div>

                {canViewFullKpis && (
                  <div className="dashboard-margin-strip">
                    <div className="dashboard-risk-subheader">
                      <Text strong>{t('dashboard:lowMarginOrders.title')}</Text>
                      <Text type="secondary">{t('dashboard:risks.marginHint')}</Text>
                    </div>
                    {(data.lowMarginOrders || []).length > 0 ? (
                      (data.lowMarginOrders || []).slice(0, 3).map((order) => (
                        <Link key={order.id} to={`/app/orders/${order.id}`} className="dashboard-low-margin-item dashboard-low-margin-item--compact">
                          <span className="dashboard-low-margin-item__body">
                            <strong>{order.displayOrderNumber || order.orderNumber}</strong>
                            <Text type="secondary">{order.customerName || '-'}</Text>
                          </span>
                          <Tag color="red">{formatLocalizedPercent(order.marginPercent || 0, i18n.language)}</Tag>
                        </Link>
                      ))
                    ) : (
                      <Text type="secondary" className="dashboard-margin-empty">{t('dashboard:lowMarginOrders.noOrders')}</Text>
                    )}
                  </div>
                )}
              </div>
            </section>
          </aside>
        </section>

        <footer className="dashboard-refresh">
          <Text type="secondary">
            {t('dashboard:lastRefresh', {
              date: lastRefreshedAt ? dayjs(lastRefreshedAt).format('YYYY-MM-DD HH:mm:ss') : '-',
            })}
          </Text>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={loadDashboardData}
          >
            {t('common:refresh')}
          </Button>
        </footer>
      </section>
    </div>
  );
};

export default Dashboard;
